import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Building2, TrendingUp, Receipt, AlertTriangle, ArrowRight, Plus,
} from "lucide-react";

const inr = (n) => `₹${Math.round(n || 0).toLocaleString("en-IN")}`;

export default function AgencyDashboard() {
  const [stats, setStats] = useState(null);
  const [topClients, setTopClients] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/agency/dashboard").then((r) => r.data).catch(() => null),
      api.get("/agency/clients").then((r) => r.data?.clients || []).catch(() => []),
    ]).then(([s, cs]) => {
      setStats(s);
      // Sort clients by revenue descending, take top 5
      setTopClients([...cs].sort((a, b) => (b.revenue_mtd || 0) - (a.revenue_mtd || 0)).slice(0, 5));
      setLoading(false);
    });
  }, []);

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="agency-dashboard">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-accent">// Agency</div>
          <h1 className="font-heading font-bold text-3xl md:text-4xl mt-2">Command centre</h1>
          <p className="text-sm text-foreground/70 mt-1">Live rollup across every client company you manage.</p>
        </div>
        <Link to="/agency/clients">
          <Button className="rounded-sm bg-logo-gradient text-white border-0 gap-1.5" data-testid="agency-goto-clients">
            <Plus className="w-4 h-4" /> Add client company
          </Button>
        </Link>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        {[
          { k: "clients_count",    label: "Client companies", icon: Building2,    val: (v) => v ?? "—", accent: "text-accent" },
          { k: "total_revenue",    label: "Total revenue",    icon: TrendingUp,   val: (v) => inr(v),   accent: "text-emerald-500" },
          { k: "invoices_count",   label: "Invoices raised",   icon: Receipt,      val: (v) => v ?? "—", accent: "text-blue-500" },
          { k: "filings_due_30d",  label: "Filings due · 30d", icon: AlertTriangle, val: (v) => v ?? "—", accent: "text-amber-500" },
        ].map((c) => {
          const Icon = c.icon;
          return (
            <div key={c.k} className="border border-border rounded-sm p-5 bg-card" data-testid={`agency-kpi-${c.k}`}>
              <Icon className={`w-5 h-5 mb-3 ${c.accent}`} />
              <div className="text-[10px] font-mono uppercase tracking-widest text-foreground/60">{c.label}</div>
              <div className="text-2xl font-heading font-bold mt-1">
                {loading ? <span className="inline-block w-16 h-6 bg-muted animate-pulse rounded-sm" /> : c.val(stats?.[c.k])}
              </div>
            </div>
          );
        })}
      </div>

      {/* Top clients */}
      <div className="border border-border rounded-sm bg-card">
        <div className="p-4 border-b border-border flex items-center justify-between">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-accent">// Top 5 clients by revenue MTD</div>
            <div className="font-heading font-semibold mt-0.5">Who's driving the numbers</div>
          </div>
          <Link to="/agency/clients" className="text-xs text-accent hover:underline flex items-center gap-1" data-testid="agency-view-all">
            View all <ArrowRight className="w-3 h-3" />
          </Link>
        </div>

        {loading && <div className="p-8 text-center text-sm text-foreground/60">Loading…</div>}

        {!loading && topClients.length === 0 && (
          <div className="p-10 text-center">
            <Building2 className="w-8 h-8 mx-auto text-foreground/30" />
            <div className="mt-3 font-heading font-semibold">No clients yet</div>
            <div className="text-sm text-foreground/60 mt-1">Add your first client company to see the rollup here.</div>
            <Link to="/agency/clients"><Button className="mt-4 rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="agency-empty-add">Add client</Button></Link>
          </div>
        )}

        {!loading && topClients.length > 0 && (
          <table className="w-full text-sm">
            <thead className="text-[10px] font-mono uppercase tracking-widest text-foreground/60 border-b border-border">
              <tr>
                <th className="text-left p-3">Client</th>
                <th className="text-right p-3">Revenue (MTD)</th>
                <th className="text-right p-3">AR outstanding</th>
                <th className="text-right p-3">Aging over 90d</th>
                <th className="text-right p-3">Filings pending</th>
              </tr>
            </thead>
            <tbody>
              {topClients.map((c) => (
                <tr key={c.id} className="border-b border-border/60 hover:bg-muted/30" data-testid={`agency-top-${c.id}`}>
                  <td className="p-3">
                    <div className="font-medium">{c.name}</div>
                    <div className="text-[10px] font-mono uppercase text-foreground/60">{c.plan || "—"}</div>
                  </td>
                  <td className="p-3 text-right font-mono">{inr(c.revenue_mtd)}</td>
                  <td className="p-3 text-right font-mono">{inr(c.ar_outstanding)}</td>
                  <td className="p-3 text-right font-mono text-amber-600">{inr(c.ar_aging?.over_90)}</td>
                  <td className="p-3 text-right">
                    {c.pending_filings > 0
                      ? <span className="text-amber-600 font-mono">{c.pending_filings}</span>
                      : <span className="text-emerald-600 font-mono">0</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Billing note */}
      <div className="border border-accent/25 bg-accent/5 rounded-sm p-4 text-sm">
        <div className="flex items-start gap-3">
          <TrendingUp className="w-5 h-5 text-accent shrink-0 mt-0.5" />
          <div>
            <div className="font-medium">Consolidated agency billing</div>
            <div className="text-foreground/75 mt-1">Your monthly bill covers every plan you provisioned for your clients. Add or remove clients any time — pro-rated automatically at the end of the cycle.</div>
          </div>
        </div>
      </div>
    </div>
  );
}
